<template>
  <div>
    <v-dialog v-model="internalValue" max-width="900px" persistent scrollable>
      <v-card class="ds-dialog">
        <!-- Header -->
        <v-card-title class="ds-dialog-header py-4">
          <v-icon color="white" class="mr-3" style="position: relative; z-index: 1;">mdi-test-tube</v-icon>
          <span class="ds-dialog-title">Solicitar Exames</span>
          <v-spacer></v-spacer>
          <v-btn icon color="white" aria-label="Ver tour" @click="startTour">
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            class="close-btn"
            @click="closeDialog"
            color="white"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <!-- Toolbar -->
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-format-text</v-icon>
                <span>Ferramentas de Formatação</span>
              </div>
            </div>

            <div class="ds-toolbar" data-tour="ex2-tools">
              <v-btn-group density="compact" variant="outlined" class="mr-2">
                <v-btn small @click="format('bold')" title="Negrito">
                  <v-icon size="18">mdi-format-bold</v-icon>
                </v-btn>
                <v-btn small @click="format('italic')" title="Itálico">
                  <v-icon size="18">mdi-format-italic</v-icon>
                </v-btn>
                <v-btn
                  small
                  @click="format('underline')"
                  title="Sublinhado"
                >
                  <v-icon size="18">mdi-format-underline</v-icon>
                </v-btn>
              </v-btn-group>

              <v-btn-group density="compact" variant="outlined" class="mr-2">
                <v-btn
                  small
                  @click="format('insertOrderedList')"
                  title="Lista numerada"
                >
                  <v-icon size="18">mdi-format-list-numbered</v-icon>
                </v-btn>
                <v-btn
                  small
                  @click="format('insertUnorderedList')"
                  title="Lista com marcadores"
                >
                  <v-icon size="18">mdi-format-list-bulleted</v-icon>
                </v-btn>
              </v-btn-group>

              <v-btn-group density="compact" variant="outlined" class="mr-2">
                <v-btn
                  small
                  @click="clearFormatting"
                  title="Limpar formatação"
                >
                  <v-icon size="18">mdi-format-clear</v-icon>
                </v-btn>
                <v-btn small @click="format('undo')" title="Desfazer">
                  <v-icon size="18">mdi-undo</v-icon>
                </v-btn>
                <v-btn small @click="format('redo')" title="Refazer">
                  <v-icon size="18">mdi-redo</v-icon>
                </v-btn>
              </v-btn-group>

              <!-- Ditado por voz -->
              <DictationButton
                ref="dictation"
                color="success"
                @transcript="onDictationTranscript"
                @recording="onDictationRecordingChange"
              />
            </div>

            <div class="mt-2 d-flex justify-space-between align-center">
              <v-chip small outlined color="success" data-tour="ex2-search">
                Use <strong>@</strong> para buscar exames
              </v-chip>

              <div>
                <v-btn
                  small
                  outlined
                  color="error"
                  @click="clearAll"
                  class="mr-2"
                  :disabled="characterCount === 0 && selectedItems.length === 0"
                >
                  <v-icon size="18" class="mr-1">mdi-delete-outline</v-icon>
                  Limpar Tudo
                </v-btn>

                <v-btn
                  small
                  outlined
                  color="success"
                  @click="openReview"
                  :disabled="characterCount === 0"
                  class="mr-2"
                >
                  <v-icon size="18" class="mr-1">mdi-file-eye-outline</v-icon>
                  Revisar
                </v-btn>

                <v-btn
                  small
                  outlined
                  color="success"
                  @click="printExamRequest"
                  :disabled="characterCount === 0"
                >
                  <v-icon size="18" class="mr-1">mdi-printer</v-icon>
                  Imprimir
                </v-btn>
              </div>
            </div>
          </section>

          <!-- Editor -->
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-text-box-outline</v-icon>
                <span>Conteúdo da Solicitação</span>
              </div>
            </div>

            <div
              class="editor-wrapper"
              :class="{ 'editor-focused': isFocused, 'editor-error': hasError }"
            >
              <div
                ref="editor"
                contenteditable="true"
                class="rich-editor"
                data-tour="ex2-editor"
                @input="updateContent"
                @keydown="handleKeydown"
                @keyup="checkAutocomplete"
                @focus="isFocused = true"
                @blur="handleBlur"
                @paste="handlePaste"
                placeholder="Digite a solicitação de exames... Use @ para buscar exames"
              ></div>

              <div v-if="apiLoading" class="editor-loading">
                <v-progress-circular
                  indeterminate
                  size="20"
                  width="2"
                  color="success"
                ></v-progress-circular>
                <span class="text-caption ml-2">Buscando exames...</span>
              </div>
            </div>

            <v-alert
              v-if="hasError"
              type="error"
              dense
              class="mt-3"
              dismissible
              @input="hasError = false"
            >
              Por favor, preencha a solicitação antes de salvar
            </v-alert>
          </section>

          <!-- Menu de Autocomplete -->
          <v-menu
            v-model="showAutocomplete"
            :close-on-content-click="false"
            :position-x="menuPosition.x"
            :position-y="menuPosition.y"
            absolute
            offset-y
          >
            <v-card class="autocomplete-card">
              <v-card-title class="py-2 px-3">
                <v-icon size="18" color="success" class="mr-2"
                  >mdi-magnify</v-icon
                >
                <span class="text-subtitle-2">Exames</span>
                <v-spacer></v-spacer>
                <v-chip x-small
                  >{{ exams.length }} resultados</v-chip
                >
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-0">
                <v-list v-if="exams.length > 0" class="autocomplete-list">
                  <v-list-item
                    v-for="(exam, index) in exams"
                    :key="index"
                    @click="openItemCidDialog(exam)"
                    :class="{ 'selected-item': index === selectedIndex }"
                    class="autocomplete-item"
                  >
                    <template v-slot:prepend>
                      <v-icon color="success">mdi-test-tube</v-icon>
                    </template>
                    <v-list-item-title>{{ exam.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="exam.description">{{
                      exam.description
                    }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
                <div v-else class="pa-4 text-center text-caption">
                  Nenhum exame encontrado
                </div>
              </v-card-text>
            </v-card>
          </v-menu>
        </v-card-text>

        <!-- Footer -->
        <v-card-actions class="ds-dialog-actions">
          <v-chip small class="footer-chip">
            <v-icon x-small left>mdi-text</v-icon>
            {{ characterCount }} caracteres
          </v-chip>
          <v-chip
            small
            color="success"
            class="footer-chip"
          >
            <v-icon x-small left>mdi-test-tube</v-icon>
            {{ selectedItems.length }} exames
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn outlined color="primary" class="ds-cancel-btn" @click="closeDialog">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-btn color="primary" depressed class="ds-save-btn" @click="saveExamRequest" :loading="isSaving" data-tour="ex2-save"
            >Salvar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Revisar (preview com letterhead) -->
    <PrescriptionReviewDialog
      v-model="showReviewDialog"
      :content="reviewContent"
      prescription-type="exam"
      :selected-items-length="selectedItems.length"
      :letterhead-id="letterheadId"
    />

    <!-- Revisão dos exames ditados por voz -->
    <DictationReviewDialog
      v-model="showDictationReview"
      :items="parsedDictationItems"
      prescription-type="exam"
      :patient-id="patientId"
      @confirm="confirmDictationItems"
      @cancel="cancelDictationReview"
    />

    <!-- Confirmação de CID do exame (busca) -->
    <ItemCidConfirmDialog
      v-model="showItemCidDialog"
      :item="pendingDirectItem"
      :patient-id="patientId"
      prescription-type="exam"
      @confirm="confirmItemCid"
      @cancel="cancelItemCid"
    />
  </div>
</template>

<script>
import axios from "axios"; // mantém para chamada EXTERNA (nutria-test...)
import http from "@/services/http";
import PrescriptionReviewDialog from "@/features/patients/prescriptions/PrescriptionReviewDialog.vue";
import DictationButton from "@/features/patients/prescriptions/components/DictationButton.vue";
import DictationReviewDialog from "@/features/patients/prescriptions/components/DictationReviewDialog.vue";
import dictationStructuredMixin from "@/features/patients/prescriptions/mixins/dictationStructuredMixin.js";
import { printPrescription } from "@/features/patients/prescriptions/utils/printPrescription.js";
import { getLetterhead } from "@/services/letterheadService";
import ItemCidConfirmDialog from "@/features/patients/prescriptions/dialogs/ItemCidConfirmDialog.vue";
import tourDialogMixin from "@/features/tour/tourDialogMixin";

export default {
  name: "ExamRequestDialog",

  mixins: [dictationStructuredMixin, tourDialogMixin],

  components: {
    PrescriptionReviewDialog,
    DictationButton,
    DictationReviewDialog,
    ItemCidConfirmDialog,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      characterCount: 0,
      isFocused: false,
      hasError: false,
      isSaving: false,
      apiLoading: false,
      showAutocomplete: false,
      searchQuery: "",
      exams: [],
      selectedItems: [],
      selectedIndex: 0,
      // Janela de confirmação de CID para exame (busca)
      showItemCidDialog: false,
      pendingDirectItem: null,
      menuPosition: { x: 0, y: 0 },
      savedRange: null,
      savedSelection: null,
      // Review / letterhead
      showReviewDialog: false,
      reviewContent: "",
      letterheadId: null,
    };
  },

  watch: {
    value(open) {
      if (open) {
        this.loadLetterhead();
        this.tourOnOpen();
      } else {
        // Garante que o microfone seja desligado ao fechar o dialog
        // por qualquer caminho (X, Cancelar, exit-without-save, success).
        this.$refs.dictation?.stop();
      }
    },
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    patientId() {
      return this.$route.params.id;
    },
    tourId() {
      return "dialog-exame";
    },
  },

  methods: {
    tourSteps() {
      return [
        {
          title: "Solicitação de exames",
          description:
            "Aqui você monta a solicitação de exames do paciente e a salva ou imprime. Vamos ver os pontos principais.",
        },
        {
          anchor: "ex2-search",
          title: "Buscar exames",
          description:
            "Digite @ dentro do editor para pesquisar exames pelo nome e inseri-los na solicitação com um clique.",
        },
        {
          anchor: "ex2-editor",
          title: "Conteúdo da solicitação",
          description:
            "Este é o corpo da solicitação. Escreva livremente e veja os exames selecionados aparecerem em destaque.",
        },
        {
          anchor: "ex2-tools",
          title: "Formatação e ditado",
          description:
            "Use estas ferramentas para formatar o texto (negrito, listas, etc.) ou ditar por voz sem precisar digitar.",
        },
        {
          anchor: "ex2-save",
          title: "Salvar",
          description:
            "Quando estiver tudo pronto, clique aqui para salvar a solicitação no prontuário do paciente.",
        },
      ];
    },

    format(command) {
      document.execCommand(command, false, null);
      this.$refs.editor.focus();
    },

    clearFormatting() {
      document.execCommand("removeFormat", false, null);
      this.$refs.editor.focus();
    },

    updateContent() {
      const content = this.$refs.editor.innerText || "";
      this.characterCount = content.length;
      this.hasError = false;
    },

    handleKeydown(e) {
      if (this.showAutocomplete) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          this.selectedIndex = Math.min(
            this.selectedIndex + 1,
            this.exams.length - 1,
          );
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        } else if (e.key === "Enter") {
          e.preventDefault();
          if (this.exams[this.selectedIndex]) {
            this.openItemCidDialog(this.exams[this.selectedIndex]);
          }
        } else if (e.key === "Escape") {
          e.preventDefault();
          this.showAutocomplete = false;
        }
      }

      if (e.ctrlKey || e.metaKey) {
        if (e.key === "b") {
          e.preventDefault();
          this.format("bold");
        } else if (e.key === "i") {
          e.preventDefault();
          this.format("italic");
        } else if (e.key === "u") {
          e.preventDefault();
          this.format("underline");
        }
      }
    },

    handleBlur() {
      setTimeout(() => {
        this.isFocused = false;
      }, 200);
    },

    handlePaste(e) {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
    },

    async checkAutocomplete(e) {
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const textNode = range.startContainer;
      const text = textNode.textContent || "";
      const cursorPos = range.startOffset;

      let atIndex = -1;
      for (let i = cursorPos - 1; i >= 0; i--) {
        if (text[i] === "@") {
          atIndex = i;
          break;
        }
        if (text[i] === " " || text[i] === "\n") {
          break;
        }
      }

      if (atIndex !== -1) {
        this.searchQuery = text.substring(atIndex + 1, cursorPos).trim();

        const rect = range.getBoundingClientRect();
        this.menuPosition = {
          x: rect.left + window.scrollX,
          y: rect.top + window.scrollY + 20,
        };

        this.showAutocomplete = true;
        this.selectedIndex = 0;

        if (this.searchQuery.length > 0) {
          await this.fetchExamsFromAPI(this.searchQuery);
        } else {
          this.exams = [];
        }
      } else {
        this.showAutocomplete = false;
      }
    },

    async fetchExamsFromAPI(query) {
      this.apiLoading = true;
      try {
        const res = await http.post(
          "/api/prescription/search-exams",
          { texto: query },
          { headers: { "Content-Type": "application/json" } },
        );

        const data = res.data;
        this.exams = data.candidatos.slice(0, 10).map((item) => ({
          name: item.descricao_encontrada,
          tuss_code: item.codigo_tuss,
          similarity: item.similaridade,
          description: `Código TUSS: ${item.codigo_tuss} | Similaridade: ${item.similaridade}%`,
        }));
      } catch (err) {
        console.error("Erro ao buscar exames:", err);
        this.exams = [];
      } finally {
        this.apiLoading = false;
      }
    },

    // Captura a posição do "@" e abre a janela de CID antes de inserir o exame.
    openItemCidDialog(exam) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        this.savedRange = range.cloneRange();

        const textNode = range.startContainer;
        const text = textNode.textContent || "";
        const cursorPos = range.startOffset;

        let atIndex = -1;
        for (let i = cursorPos - 1; i >= 0; i--) {
          if (text[i] === "@") {
            atIndex = i;
            break;
          }
        }

        this.savedSelection = { textNode, text, cursorPos, atIndex };
      }
      this.pendingDirectItem = exam;
      this.showAutocomplete = false;
      this.showItemCidDialog = true;
    },
    confirmItemCid(cidIds) {
      // `condition_ids` = ids de patient_conditions (é o que o PatientCidSelector
      // emite e o que PrescriptionController valida). Antes ia como
      // `patient_cid_ids`, chave da estrutura morta, e o backend descartava.
      const item = { ...this.pendingDirectItem, condition_ids: cidIds };
      this.showItemCidDialog = false;
      this.pendingDirectItem = null;
      this.insertExamDirectly(item);
    },
    cancelItemCid() {
      this.showItemCidDialog = false;
      this.pendingDirectItem = null;
      this.savedRange = null;
      this.savedSelection = null;
    },
    // A seleção do "@" já foi capturada em openItemCidDialog.
    insertExamDirectly(exam) {
      const examWithDetails = {
        ...exam,
        type: "exam",
        position: this.selectedItems.length,
        timestamp: new Date().toISOString(),
      };

      this.selectedItems.push(examWithDetails);
      this.insertItemInEditor(examWithDetails);
      this.showAutocomplete = false;

      this.$nextTick(() => {
        this.updateContent();
      });

      setTimeout(() => {
        this.$refs.editor.focus();
        this.savedRange = null;
        this.savedSelection = null;
      }, 100);
    },

    insertItemInEditor(item) {
      const editor = this.$refs.editor;
      if (!editor) return;

      const itemText = `${item.name}`;

      const selection = window.getSelection();
      if (this.savedRange && this.savedSelection) {
        try {
          selection.removeAllRanges();
          selection.addRange(this.savedRange);

          const { textNode, text, cursorPos, atIndex } = this.savedSelection;

          if (atIndex !== -1 && textNode.nodeType === Node.TEXT_NODE) {
            const wrapper = document.createElement("div");
            wrapper.className = "exam-line";
            wrapper.setAttribute("data-tuss", item.tuss_code);
            wrapper.setAttribute("data-item-index", item.position);
            wrapper.setAttribute("data-item-type", item.type);
            wrapper.style.display = "flex";
            wrapper.style.alignItems = "center";
            wrapper.style.justifyContent = "space-between";
            wrapper.style.gap = "8px";

            const textSpan = document.createElement("span");
            textSpan.style.flex = "1";
            textSpan.textContent = itemText;

            const deleteBtn = document.createElement("button");
            deleteBtn.innerHTML = "×";
            deleteBtn.type = "button";
            deleteBtn.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.removeItem(item.position);
            };

            wrapper.appendChild(textSpan);
            wrapper.appendChild(deleteBtn);

            const before = text.substring(0, atIndex);
            const after = text.substring(cursorPos);

            textNode.textContent = before;
            const parent = textNode.parentNode;

            if (before.trim() !== "") {
              const br1 = document.createElement("br");
              parent.insertBefore(br1, textNode.nextSibling);
              parent.insertBefore(wrapper, br1.nextSibling);
            } else {
              parent.insertBefore(wrapper, textNode.nextSibling);
            }

            const br2 = document.createElement("br");
            parent.insertBefore(br2, wrapper.nextSibling);

            if (after) {
              const afterText = document.createTextNode(after);
              parent.insertBefore(afterText, br2.nextSibling);

              const newRange = document.createRange();
              newRange.setStart(afterText, 0);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
            } else {
              const spaceText = document.createTextNode(" ");
              parent.insertBefore(spaceText, br2.nextSibling);
              const newRange = document.createRange();
              newRange.setStart(spaceText, 1);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
          }
        } catch (error) {
          console.error("Erro ao inserir item:", error);

          const wrapper = document.createElement("div");
          wrapper.className = "exam-line";
          wrapper.setAttribute("data-tuss", item.tuss_code);
          wrapper.setAttribute("data-item-index", item.position);
          wrapper.setAttribute("data-item-type", item.type);
          wrapper.style.display = "flex";
          wrapper.style.alignItems = "center";
          wrapper.style.justifyContent = "space-between";

          const textSpan = document.createElement("span");
          textSpan.style.flex = "1";
          textSpan.textContent = itemText;

          const deleteBtn = document.createElement("button");
          deleteBtn.innerHTML = "×";
          deleteBtn.type = "button";
          deleteBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.removeItem(item.position);
          };

          wrapper.appendChild(textSpan);
          wrapper.appendChild(deleteBtn);

          editor.appendChild(document.createElement("br"));
          editor.appendChild(wrapper);
          editor.appendChild(document.createElement("br"));
        }
      }
    },

    removeItem(position) {
      this.selectedItems = this.selectedItems.filter(
        (item) => item.position !== position,
      );

      const itemWrapper = this.$refs.editor.querySelector(
        `[data-item-index="${position}"]`,
      );
      if (itemWrapper && itemWrapper.parentNode) {
        const parent = itemWrapper.parentNode;
        const nextSibling = itemWrapper.nextSibling;
        const prevSibling = itemWrapper.previousSibling;

        if (nextSibling && nextSibling.nodeName === "BR")
          parent.removeChild(nextSibling);
        if (prevSibling && prevSibling.nodeName === "BR")
          parent.removeChild(prevSibling);

        parent.removeChild(itemWrapper);
      }

      this.$nextTick(() => {
        this.updateContent();
      });
    },

    async clearAll() {
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar limpeza",
        message: "Tem certeza que deseja limpar toda a solicitação?",
        hint: "Esta ação não pode ser desfeita.",
        confirmText: "Limpar Tudo",
      });
      if (!ok) return;
      this.resetForm();
      this.$refs.editor?.focus();
    },

    printExamRequest() {
      try {
        printPrescription({
          content: this.$refs.editor.innerHTML,
          prescriptionType: "exam",
          selectedItemsLength: this.selectedItems.length,
        });
      } catch (error) {
        this.$confirm({ type: "warning", title: "Atenção", message: "Não há conteúdo." });
      }
    },

    openReview() {
      const content = this.$refs.editor?.innerHTML || "";
      if (!content || content.trim() === "") {
        this.$confirm({ type: "warning", title: "Atenção", message: "Não há conteúdo." });
        return;
      }
      this.reviewContent = content;
      this.showReviewDialog = true;
    },

    onDictationToggle(isRecording) {
      if (isRecording) {
        const editor = this.$refs.editor;
        if (!editor) return;
        editor.focus();
        const selection = window.getSelection();
        const inEditor =
          selection.rangeCount > 0 &&
          editor.contains(selection.getRangeAt(0).startContainer);
        if (!inEditor) {
          const range = document.createRange();
          range.selectNodeContents(editor);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    },

    insertDictatedText(text) {
      if (!text) return;
      const editor = this.$refs.editor;
      if (!editor) return;
      editor.focus();
      document.execCommand("insertText", false, text.trim() + " ");
      this.updateContent();
    },

    async loadLetterhead() {
      try {
        const lh = await getLetterhead();
        this.letterheadId = lh?.id || null;
      } catch (err) {
        this.letterheadId = null;
      }
    },

    async closeDialog() {
      if (this.characterCount > 0) {
        const ok = await this.$confirm({
          type: "warning",
          title: "Sair sem salvar?",
          message:
            "Você tem conteúdo não salvo. Deseja realmente sair sem salvar?",
          confirmText: "Sair sem Salvar",
        });
        if (!ok) return;
      }
      this.$emit("input", false);
      this.resetForm();
    },

    resetForm() {
      this.$refs.editor.innerHTML = "";
      this.selectedItems = [];
      this.exams = [];
      this.characterCount = 0;
      this.hasError = false;
      this.savedRange = null;
      this.savedSelection = null;
      this.dictationBuffer = "";
      this.parsedDictationItems = [];
      this.showDictationReview = false;
    },

    async saveExamRequest() {
      const content = this.$refs.editor.innerHTML;

      if (!content || content.trim() === "") {
        this.hasError = true;
        return;
      }

      if (this.selectedItems.length === 0) {
        this.$confirm({
          type: "warning",
          title: "Atenção",
          message: "Por favor, selecione pelo menos um exame usando @",
        });
        return;
      }

      this.isSaving = true;

      try {
        const patientId = this.$route.params.id;

        // ✅ doctorId via Vuex
        let doctorId = this.$store.getters["auth/userId"];
        if (!doctorId) {
          await this.$store.dispatch("auth/ensureAuth");
          doctorId = this.$store.getters["auth/userId"];
        }

        if (!patientId) {
          this.$confirm({
            type: "error",
            title: "Erro",
            message: "ID do paciente não encontrado",
          });
          return;
        }

        if (!doctorId) {
          this.$confirm({
            type: "error",
            title: "Erro",
            message: "ID do médico não encontrado",
          });
          return;
        }

        // Uma única requisição que cria:
        // - 1 Prescription (prescription_type = 'exam')
        // - N PrescriptionItem (um por exame, item_type = 'exam')
        // - N Exam (um por item, já com prescription_item_id vinculado)
        // Assim cada exame entra individualmente na tabela exams e pode ser
        // atualizado depois com os resultados.
        const prescriptionData = {
          patient_id: patientId,
          prescription_type: "exam",
          full_content: content,
          status: "active",
          items: this.selectedItems.map((exam) => ({
            item_type: "exam",
            medication_name: exam.name,
            tuss_code: exam.tuss_code || null,
            notes: exam.observacoes || null,
            condition_ids: exam.condition_ids || [],
          })),
        };

        const response = await http.post(
          `/api/prescription/create`,
          prescriptionData,
          { headers: { "Content-Type": "application/json" } },
        );

        this.$emit("saved", response.data);

        const count = this.selectedItems.length;
        this.isSaving = false;
        await this.$confirm({
          type: "success",
          title: "Sucesso!",
          message: `Solicitação salva com sucesso!\n\n${count} ${
            count === 1 ? "exame" : "exames"
          }`,
        });
        this.$emit("input", false);
        this.resetForm();
        return;
      } catch (error) {
        console.error("❌ Erro ao salvar:", error);

        let errorMessage = "Erro ao salvar. Tente novamente.";

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.errors) {
          errorMessage = Object.values(error.response.data.errors)
            .flat()
            .join("\n");
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$confirm({ type: "error", title: "Erro", message: errorMessage });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
/* ═══ Design system dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.close-btn {
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* ═══ Editor ═══ */
.editor-wrapper {
  border: 2px solid var(--ds-blue-100);
  border-radius: 8px;
  background: var(--ds-surface-white);
  transition: all 0.25s ease;
  position: relative;
  box-shadow: 0 2px 8px rgba(31, 77, 63, 0.05);
}

.editor-wrapper.editor-focused {
  border-color: var(--ds-primary);
  box-shadow: 0 0 0 3px rgba(31, 77, 63, 0.12);
}

/* ═══ Toolbar de formatação ═══ */
.ds-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #F4F9F6;
  border: 1px solid var(--ds-blue-100);
  border-radius: 8px;
}

.ds-toolbar :deep(.v-btn) {
  text-transform: none;
}

.ds-cancel-btn,
.ds-save-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  height: 40px !important;
  border-radius: 8px !important;
}

.ds-save-btn {
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.22) !important;
}

.footer-chip {
  font-size: 11px !important;
  font-weight: 600 !important;
}

.editor-wrapper.editor-error {
  border-color: rgb(var(--v-theme-error));
}

.rich-editor {
  min-height: 160px;
  max-height: 34vh;
  width: 100%;
  padding: 16px;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ds-text);
}

.rich-editor:focus {
  outline: none;
}

.rich-editor:empty:before {
  content: attr(placeholder);
  color: var(--ds-gray-light);
  pointer-events: none;
  font-style: italic;
}

.editor-loading {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.rich-editor :deep(.exam-line) {
  display: block;
  background-color: var(--ds-green-surface);
  padding: 8px 12px;
  border-radius: 4px;
  margin-top: 4px;
  margin-bottom: 4px;
  border-left: 4px solid var(--ds-green);
  font-weight: 500;
  line-height: 1.5;
  transition: background-color 0.2s ease;
}

.rich-editor :deep(.exam-line:hover) {
  background-color: var(--ds-green-200);
}

.selected-item {
  background-color: rgba(var(--v-theme-success), 0.15) !important;
}

.rich-editor :deep(strong) {
  font-weight: 700;
}

.rich-editor :deep(em) {
  font-style: italic;
}

.rich-editor :deep(u) {
  text-decoration: underline;
}

.rich-editor :deep(ul),
.rich-editor :deep(ol) {
  padding-left: 24px;
  margin: 8px 0;
}

.rich-editor :deep(li) {
  margin: 4px 0;
}

.exam-line {
  position: relative;
}
</style>
